import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, Code as CodeIcon, History, Hourglass, ListOrdered } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIsPhone } from '@/hooks/useIsPhone'
import { getTimeframeSeconds } from '@/utils/chartUtils'
// By path, not the barrel: the barrel exports the Workbench, which mounts this pane, and a
// cycle through it is one more thing for the bundler to warn about.
import { UpgradeOverlay } from '../UpgradeOverlay/UpgradeOverlay'
import BotBacktestCode from '../../bot/components/BotBacktestCode'
import type { BotCode } from '../../bot/api'
import type { OrderData, OrderEventData } from '../../bot/types/chartData'
import { buildWalkthroughTrades, type WalkthroughTrade } from './walkthroughTrades'
import { buildPendingOrders, type PendingOrderRow } from './walkthroughPending'

const MIN_HEIGHT = 120
const DEFAULT_HEIGHT = 132
/** Never a full-screen table: this is a validation receipt, not the Backtest tab. */
const MAX_HEIGHT_FRACTION = 0.4
/**
 * A phone gives the pane less, because the chart is the thing being watched and the phone's
 * transport band now takes a row of its own out of the same column. At 0.3 the chart still
 * holds above ~55% of the viewport with the pane dragged to its largest, and around ~72% at
 * the pane's default (which on a phone is its minimum).
 */
const PHONE_MAX_HEIGHT_FRACTION = 0.3

/**
 * The Open tab trades the two columns that are always a dash on an open trade — Close time and
 * Exit — for the one thing that moves: the price its floating pips are measured against.
 */
const OPEN_COLUMNS = [
  'Ticket',
  'Open time',
  'Type',
  'Volume',
  'Entry',
  'Stop loss',
  'Take profit',
  'Current',
  'P/L (pips)',
] as const

const CLOSED_COLUMNS = [
  'Ticket',
  'Open time',
  'Close time',
  'Type',
  'Volume',
  'Entry',
  'Stop loss',
  'Take profit',
  'Exit',
  'Exits',
  'P/L (pips)',
] as const

/**
 * The Pending tab: orders resting at the playhead. "Order" rather than "Ticket" because the
 * value is the id the strategy code sees (what `buy_limit()` returned), not the pane's #n
 * position numbering; the engine ticket is on the row's title.
 */
const PENDING_COLUMNS = [
  'Order',
  'Placed',
  'Type',
  'Price',
  'Volume',
  'Stop loss',
  'Take profit',
  'Expires',
  'Distance (pips)',
] as const

type PaneTab = 'open' | 'pending' | 'closed' | 'code'

export interface WalkthroughTradesPaneProps {
  /**
   * Every order event of the run that is on the chart — see useWalkthroughOrders, which owns
   * both the fetch and the "is this position in view at all?" question.
   */
  orders: OrderData[]
  /** Replay cursor, unix seconds. Rows appear as it passes them. */
  playheadTs: number
  /**
   * The run's pending-order and netting events (see useWalkthroughOrders). Feeds the Pending
   * tab and the scale-in / partial-exit figures on the other two. Legacy runs have none.
   */
  activity?: OrderEventData[]
  /** The run's timeframe, for an expiring order's bars-left figure. */
  timeframe?: string
  /** Last revealed close, for floating pips on open trades. */
  markPrice: number | null
  /** From backtest_config.pip_size. */
  pipSize: number
  /** Price decimals, from the symbol's point size. */
  precision: number
  /**
   * Bring a trade into view on the chart. Rows are inert without it.
   *
   * Takes the trade's ENTRY time, and the handler behind it only scrolls — it must not move the
   * playhead. The replay keeps its place: looking at a trade is not the same act as winding the
   * run back to it. And reveal is what decides whether a trade reads as open or closed, so
   * rewinding to a closed trade's entry would reopen it and move the very row that was clicked
   * onto the other tab.
   */
  onShowTrade?: (entryTs: number) => void
  /**
   * The playhead has reached the end of the run.
   *
   * Lands the pane on Closed trades, once, on the edge — "no open trades right now" is the
   * wrong thing to be looking at when the run has finished. Once only, so a tab the user picks
   * afterwards is not dragged back.
   */
  atEnd?: boolean
  /**
   * The bot's current code, for the Code tab — which is the Backtest tab's Code tab, chip,
   * disclaimer and lock included. The pane only shows it; whether the run is still that code's
   * run is `run.codeId`'s question, answered in the session hook.
   */
  currentCode?: BotCode | null
  /** Whether the bot has generated code at all; without it the Code tab shows the empty label. */
  hasCode?: boolean
  /**
   * Outside a walkthrough: the Code tab alone, collapsed until opened (PROD-3133, as in the
   * prototype's "Code generated" state). There are no trades to list.
   */
  codeOnly?: boolean
  /**
   * The code changed since the Code tab was last looked at: a muted dot on the tab, as in the
   * prototype. Muted on purpose: it says something moved, not whether it is good or bad.
   */
  codeUnseen?: boolean
  /** The Code tab is the pane's tab, which is what lowers the dot (never a timer). */
  onCodeSeen?: () => void
  className?: string
}

function formatPips(v: number | null): string {
  if (v == null) return '—'
  const sign = v > 0 ? '+' : v < 0 ? '−' : ''
  return `${sign}${Math.abs(v).toFixed(1)}`
}

const MOMENT_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' })
// h23, not `hour12: false`: some engines read the latter as h24 and print midnight as 24:00:00.
const MOMENT_TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

/**
 * A trade's moment: "28 Apr · 16:30:00".
 *
 * No year — a run's trades are days apart at most, so it would be the same on every row — and
 * seconds, because the open and close of one trade can sit inside the same minute.
 * Not `formatDateLabel` + `formatTimeLabel` from chartUtils: two calls, and the first carries
 * the year.
 */
function formatMoment(ts: number | null): string {
  if (ts == null) return '—'
  const d = new Date(ts * 1000)
  return `${MOMENT_DATE.format(d)} · ${MOMENT_TIME.format(d)}`
}

function formatLots(v: number | null): string {
  return v == null ? '—' : v.toFixed(2)
}

function PendingRows({
  rows,
  precision,
  onShowTrade,
  selectedTicket,
  onSelect,
}: {
  rows: PendingOrderRow[]
  precision: number
  onShowTrade?: (placedTs: number) => void
  selectedTicket: number | null
  onSelect: (ticket: number) => void
}) {
  const price = (v: number | null) => (v == null ? '—' : v.toFixed(precision))
  const show = (r: PendingOrderRow) => {
    onSelect(r.ticket)
    onShowTrade?.(r.placedTs)
  }
  return (
    <>
      {rows.map((r) => (
        <tr
          key={`pending:${r.ticket}`}
          title={`order ticket ${r.ticket}`}
          onClick={onShowTrade ? () => show(r) : undefined}
          onKeyDown={
            onShowTrade
              ? (e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  e.preventDefault()
                  show(r)
                }
              : undefined
          }
          tabIndex={onShowTrade ? 0 : undefined}
          aria-label={onShowTrade ? `Show order #${r.orderId} on the chart` : undefined}
          className={cn(
            'border-b border-border last:border-b-0',
            onShowTrade && 'cursor-pointer hover:bg-muted/50',
            selectedTicket === r.ticket && 'bg-muted',
          )}
        >
          <td className="px-3 py-2 text-caption tabular-nums text-muted-foreground whitespace-nowrap">
            #{r.orderId}
          </td>
          <td className="px-3 py-2 text-caption tabular-nums text-muted-foreground whitespace-nowrap">
            {formatMoment(r.placedTs)}
          </td>
          <td
            className={cn(
              'px-3 py-2 text-caption font-semibold whitespace-nowrap',
              r.side === 'BUY' ? 'text-success' : 'text-danger',
            )}
          >
            {r.label}
          </td>
          <td className="px-3 py-2 text-body tabular-nums">{price(r.price)}</td>
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{formatLots(r.volume)}</td>
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{price(r.sl)}</td>
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{price(r.tp)}</td>
          <td className="px-3 py-2 text-caption tabular-nums text-muted-foreground whitespace-nowrap">
            {r.expiresAt == null
              ? 'GTC'
              : `${r.barsLeft} bar${r.barsLeft === 1 ? '' : 's'} · ${formatMoment(r.expiresAt)}`}
          </td>
          <td
            className={cn(
              'px-3 py-2 text-body tabular-nums',
              r.distancePips == null ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {formatPips(r.distancePips)}
          </td>
        </tr>
      ))}
    </>
  )
}

function TradeRows({
  rows,
  live,
  markPrice,
  precision,
  onShowTrade,
  selectedTicket,
  onSelect,
}: {
  rows: WalkthroughTrade[]
  /** Open-tab rows: Current in place of Close time and Exit — see OPEN_COLUMNS. */
  live: boolean
  markPrice: number | null
  precision: number
  onShowTrade?: (entryTs: number) => void
  selectedTicket: number | null
  onSelect: (ticket: number) => void
}) {
  const price = (v: number | null) => (v == null ? '—' : v.toFixed(precision))

  const show = (t: WalkthroughTrade) => {
    onSelect(t.ticket)
    onShowTrade?.(t.entryTs)
  }

  return (
    <>
      {rows.map((t) => (
        <tr
          key={String(t.openOrderId)}
          onClick={onShowTrade ? () => show(t) : undefined}
          onKeyDown={
            onShowTrade
              ? (e) => {
                  // A <tr> is not a button, so the pointer affordance has to be matched by hand
                  // rather than inherited. Cheap here, and the shared TradesTable's click-only
                  // row is not a gap worth copying into new code.
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  e.preventDefault()
                  show(t)
                }
              : undefined
          }
          tabIndex={onShowTrade ? 0 : undefined}
          aria-label={onShowTrade ? `Show trade #${t.ticket} on the chart` : undefined}
          className={cn(
            'border-b border-border last:border-b-0',
            onShowTrade && 'cursor-pointer hover:bg-muted/50',
            // The same fill as the active tab pill: one "this is the one" language in the pane.
            selectedTicket === t.ticket && 'bg-muted',
          )}
        >
          <td className="px-3 py-2 text-caption tabular-nums text-muted-foreground whitespace-nowrap">
            #{t.ticket}
          </td>
          {/* The open moment agrees with where a row click lands. The close only exists on the
              History tab, where every row has one. */}
          <td className="px-3 py-2 text-caption tabular-nums text-muted-foreground whitespace-nowrap">
            {formatMoment(t.entryTs)}
          </td>
          {!live && (
            <td className="px-3 py-2 text-caption tabular-nums text-muted-foreground whitespace-nowrap">
              {formatMoment(t.exitTs)}
            </td>
          )}
          <td
            className={cn(
              'px-3 py-2 text-caption font-semibold',
              t.type === 'Buy' ? 'text-success' : 'text-danger',
            )}
          >
            {t.type}
          </td>
          {/* Lots, not money: what a scale-in or a partial exit changes while the trade runs. */}
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{formatLots(t.volume)}</td>
          <td className="px-3 py-2 text-body tabular-nums">{price(t.entry)}</td>
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{price(t.sl)}</td>
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{price(t.tp)}</td>
          {/* The same close the floating pips are taken against, so the two move together. */}
          <td className="px-3 py-2 text-body tabular-nums">
            {price(live ? markPrice : t.exit)}
          </td>
          {!live && (
            <td
              className="px-3 py-2 text-caption tabular-nums text-muted-foreground"
              title={
                t.partialExits && t.partialExits.length > 0
                  ? t.partialExits
                      .map((x) => `${formatMoment(x.time)} · ${x.volume.toFixed(2)} @ ${x.price.toFixed(precision)}`)
                      .join('\n')
                  : undefined
              }
            >
              {t.exits > 0 ? t.exits : '—'}
            </td>
          )}
          <td
            className={cn(
              'px-3 py-2 text-body tabular-nums',
              t.pips == null
                ? 'text-muted-foreground'
                : t.pips >= 0
                  ? 'text-success'
                  : 'text-danger',
            )}
          >
            {formatPips(t.pips)}
          </td>
        </tr>
      ))}
    </>
  )
}

/**
 * The walkthrough's trade list: what the generated code is holding, and what it has closed,
 * as of the replay playhead — and, on its third tab, that code, exactly as the Backtest tab
 * shows it.
 *
 * Pips only, no money, no totals — see walkthroughTrades.ts for why. Fixed-ceiling height so
 * it can never grow into a backtest-sized table; the Code tab lives inside the same ceiling.
 */
export default function WalkthroughTradesPane({
  orders,
  playheadTs,
  activity = [],
  timeframe,
  markPrice,
  pipSize,
  precision,
  onShowTrade,
  atEnd = false,
  currentCode = null,
  hasCode = false,
  codeOnly = false,
  codeUnseen = false,
  onCodeSeen,
  className,
}: WalkthroughTradesPaneProps) {
  const isPhone = useIsPhone()
  const [collapsed, setCollapsed] = useState(codeOnly)
  const [tab, setTab] = useState<PaneTab>(codeOnly ? 'code' : 'open')
  const [height, setHeight] = useState(isPhone ? MIN_HEIGHT : DEFAULT_HEIGHT)
  /**
   * The trade last pointed at on the chart, by TICKET.
   *
   * A ticket rather than a list index: it is stable across the open→closed transition, so the
   * highlight follows a trade onto the other tab — and it self-clears, because a scrub back that
   * un-reveals the trade simply leaves no row matching it. Nothing has to remember to reset it.
   */
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null)
  /** The pending order last pointed at, by its engine ticket (its own namespace). */
  const [selectedOrderTicket, setSelectedOrderTicket] = useState<number | null>(null)
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)

  const maxHeight = () =>
    Math.round(window.innerHeight * (isPhone ? PHONE_MAX_HEIGHT_FRACTION : MAX_HEIGHT_FRACTION))

  // Rotating a tablet into phone width, or resizing a desktop window down, can leave a height
  // that is now most of the viewport. Clamp on the crossover rather than waiting for the next
  // drag to notice.
  useEffect(() => {
    if (!isPhone) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconciling a stored size against a breakpoint change IS the synchronization
    setHeight((h) => Math.min(h, maxHeight()))
    // maxHeight closes over isPhone, which is the only thing that changes it here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhone])

  // Land on History when the run finishes. On the false→true EDGE only: fired on every render
  // while `atEnd` held it would keep pulling the user off a tab they had chosen afterwards, and
  // the point is only to not leave them staring at "no open trades right now" at the finish.
  // Which is also why it moves only OFF Open: someone reading the code when the run ends is not
  // looking at anything empty, and keeps their place.
  const prevAtEndRef = useRef(atEnd)
  useEffect(() => {
    const wasAtEnd = prevAtEndRef.current
    prevAtEndRef.current = atEnd
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a one-shot edge signal: moving the tab IS the effect's purpose
    if (atEnd && !wasAtEnd) setTab((t) => (t === 'open' ? 'closed' : t))
  }, [atEnd])

  // Selected counts as looked at, collapsed or not, as in the prototype.
  useEffect(() => {
    if (tab === 'code' && codeUnseen) onCodeSeen?.()
  }, [tab, codeUnseen, onCodeSeen])

  const { open, closed } = useMemo(
    () => buildWalkthroughTrades(orders, playheadTs, pipSize, markPrice, activity),
    [orders, playheadTs, pipSize, markPrice, activity],
  )
  const tfSec = timeframe ? getTimeframeSeconds(timeframe) : 0
  const pending = useMemo(
    () => buildPendingOrders(activity, playheadTs, markPrice, pipSize, tfSec),
    [activity, playheadTs, markPrice, pipSize, tfSec],
  )

  const onResizeDown = (e: React.PointerEvent) => {
    e.preventDefault()
    dragRef.current = { startY: e.clientY, startH: height }
    const max = maxHeight()
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      setHeight(Math.min(max, Math.max(MIN_HEIGHT, d.startH + (d.startY - ev.clientY))))
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const countBadge = (count: number) => (
    <span className={cn('tabular-nums', count > 0 ? 'opacity-100' : 'opacity-70')}>{count}</span>
  )
  // The Backtest tab's version chip, verbatim: the chip is what makes this THE Code tab rather
  // than a lookalike. Nothing at all while the code carries no version.
  const versionBadge =
    currentCode?.version != null ? (
      <span className="font-mono tabular-nums opacity-70">v{currentCode.version}</span>
    ) : null

  const allTabs: Array<{ id: PaneTab; label: string; Icon: typeof ListOrdered; badge: ReactNode }> = [
    { id: 'open', label: 'Open', Icon: ListOrdered, badge: countBadge(open.length) },
    { id: 'pending', label: 'Pending', Icon: Hourglass, badge: countBadge(pending.length) },
    { id: 'closed', label: 'History', Icon: History, badge: countBadge(closed.length) },
    {
      id: 'code',
      label: 'Code',
      Icon: CodeIcon,
      badge: (
        <>
          {versionBadge}
          {codeUnseen && <span data-code-unseen className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />}
        </>
      ),
    },
  ]
  const tabs = codeOnly ? allTabs.filter((t) => t.id === 'code') : allTabs
  const what = codeOnly ? 'code' : 'trades'
  const rows = tab === 'open' ? open : closed
  const isEmpty = tab === 'pending' ? pending.length === 0 : rows.length === 0
  const emptyText =
    tab === 'open'
      ? 'No open trades at this point in the replay.'
      : tab === 'pending'
        ? 'No pending orders at this point in the replay.'
        : 'No closed trades yet — press play to watch your strategy trade.'
  const columns = tab === 'open' ? OPEN_COLUMNS : tab === 'pending' ? PENDING_COLUMNS : CLOSED_COLUMNS
  // Collapse / expand, at the pane's right edge as in the prototype. The floating Ask AI pill can
  // sit over it on a phone while the pane is collapsed; the pill is the user's to drag away.
  // On a phone a 32px target round a 16px arrow, 4px off the edge, so the arrow's ink lands 12px
  // in, on the table's own right padding (the prototype's numbers).
  const toggle = (
    <button
      type="button"
      onClick={() => setCollapsed((v) => !v)}
      title={collapsed ? `Expand ${what}` : `Collapse ${what}`}
      aria-label={collapsed ? `Expand ${what}` : `Collapse ${what}`}
      className={cn(
        'shrink-0 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer',
        isPhone ? 'h-8 w-8 mr-1' : 'h-6 w-6 mr-4',
      )}
    >
      {collapsed ? <ChevronUp size={isPhone ? 16 : 12} strokeWidth={2} /> : <ChevronDown size={isPhone ? 16 : 12} strokeWidth={2} />}
    </button>
  )

  return (
    <div className={cn('shrink-0 flex flex-col bg-card', className)}>
      {/* A hairline to look at, with a ±5px invisible grab area hanging off it: a 1px target is
          not a thing a pointer can catch, and a 4px visible bar reads as chrome the pane does not
          need. Not the shared ResizeHandle — that one is mouse-event-only, so adopting it would
          lose the touch drag this pane was tuned for. */}
      {!collapsed && (
        <div
          role="separator"
          aria-orientation="horizontal"
          onPointerDown={onResizeDown}
          onDoubleClick={() => setHeight(isPhone ? MIN_HEIGHT : DEFAULT_HEIGHT)}
          className="relative shrink-0 h-px w-full cursor-row-resize touch-none bg-border
                     hover:bg-primary/40 transition-colors
                     before:absolute before:left-0 before:right-0 before:-top-[5px]
                     before:-bottom-[5px] before:content-['']"
        />
      )}

      <div
        className={cn(
          'flex items-center shrink-0 h-11 bg-card',
          collapsed && 'border-t border-border',
        )}
      >
        {/* THE TAB ROW AND THE TABLE UNDER IT SHARE ONE CONTENT EDGE. The two are stacked and read
            as one block, so the eye expects "Open trades" to start where "Ticket" starts — and it
            did not, by 14px: the strip carried px-4 (16) and each tab is a pill carrying px-2.5
            (10) of its own, while the table's th carries px-3 (12). Reconciled rather than nudged:
            the strip takes pl-3 to match the table's token exactly, and the first tab takes
            -ml-2.5 to negate its own pill padding exactly. Both numbers ARE the token they cancel,
            so neither drifts if the other is retuned. pr-4 is left alone — only the left edge is
            in question. Known consequence: the active pill's background now starts 2px off the
            pane edge, because a pill wrapping 10px around text that must sit at 12 cannot also be
            inset. */}
        <div
          className={cn(
            'flex-1 flex items-center gap-0.5 min-w-0 pl-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            // A phone trims the right gutter only, leaving the tabs the width, as the prototype does.
            isPhone ? 'pr-1' : 'pr-4',
          )}
        >
          {tabs.map(({ id, label, Icon, badge }, i) => {
            const active = tab === id
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setTab(id)
                  setCollapsed(false)
                }}
                className={cn(
                  'shrink-0 flex items-center gap-1.5 py-1 px-2.5 rounded-md text-caption transition-colors cursor-pointer',
                  i === 0 && '-ml-2.5',
                  active
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                )}
              >
                <Icon size={11} strokeWidth={1.5} />
                <span>{label}</span>
                {badge}
              </button>
            )
          })}
        </div>

        {toggle}
      </div>

      {!collapsed && (
        <div
          style={{ height }}
          className={cn(
            'min-h-0 border-t border-border',
            // The code scrolls inside its own viewer, so the floating Wrap/Copy stay put — and
            // `relative` is HERE, not on the pane root, so the lock covers the content and never
            // the tab strip. Both exactly as the Backtest tab has them.
            tab === 'code' ? 'relative overflow-hidden' : 'overflow-y-auto',
          )}
        >
          {tab === 'code' ? (
            <>
              {/* THE BACKTEST TAB'S CODE TAB, THE SAME COMPONENT: shiki colouring, the gutter,
                  Wrap and Copy, the empty label, and the risk disclaimer until it is acknowledged
                  (one localStorage key, so acknowledging it on either tab covers both). Keyed by
                  cid as there, so regenerated code arrives as a fresh mount. One difference in
                  WHEN a mount happens: this pane unmounts its content on collapse, the backtest's
                  does not, so an unacknowledged disclaimer also re-opens on expand.
                  The lock is the backtest's too, and it is needed here: the server does not gate
                  walkthroughs by plan, so a free user reaches this pane with the code behind it.
                  UpgradeOverlay renders nothing for anyone else. */}
              <BotBacktestCode
                key={currentCode?.cid ?? 'no-code'}
                currentCode={currentCode}
                hasCode={hasCode}
              />
              {hasCode && (
                <UpgradeOverlay
                  message="Upgrade to see your strategy code"
                  className="backdrop-grayscale"
                />
              )}
            </>
          ) : isEmpty ? (
            <div className="flex items-center justify-center h-full px-4 text-center text-caption text-muted-foreground">
              {emptyText}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* A floor on a phone so the columns scroll sideways rather than crush into
                  unreadable slivers: History's nine measure ~727px at the caption size, so this
                  is that width, not a guess — a bigger number would force a sideways scroll on a
                  desktop whose chart column could have fitted the table. Harmless above it —
                  `w-full` already exceeds it. Open's eight measure ~612px, plus ~20 for the
                  widest date ("13 Sept" against "7 Oct"); held to History's floor, it scrolled
                  sideways even in a desktop chart column. */}
              <table
                className={cn(
                  'w-full border-collapse',
                  tab === 'open' ? 'min-w-[44rem]' : tab === 'pending' ? 'min-w-[48rem]' : 'min-w-[54rem]',
                )}
              >
                <thead>
                  <tr className="border-b border-border">
                    {columns.map((c) => (
                      <th
                        key={c}
                        className="px-3 py-1.5 text-left text-caption text-muted-foreground font-medium whitespace-nowrap"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tab === 'pending' ? (
                    <PendingRows
                      rows={pending}
                      precision={precision}
                      onShowTrade={onShowTrade}
                      selectedTicket={selectedOrderTicket}
                      onSelect={setSelectedOrderTicket}
                    />
                  ) : (
                  <TradeRows
                    rows={rows}
                    live={tab === 'open'}
                    markPrice={markPrice}
                    precision={precision}
                    onShowTrade={onShowTrade}
                    selectedTicket={selectedTicket}
                    onSelect={setSelectedTicket}
                  />
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
